/** Parser de archivos `.cube` (Adobe LUT 3D) y subida a textura 3D WebGL2. */

export interface CubeLut {
  size: number;
  data: Float32Array;  // longitud = size^3 * 3
  title: string;
}

export function parseCubeLut(text: string): CubeLut {
  const lines = text.split(/\r?\n/);
  let size = 0;
  let title = "";
  const values: number[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    if (line.startsWith("TITLE")) {
      title = line.replace(/^TITLE\s*/i, "").replace(/^"|"$/g, "");
      continue;
    }
    if (line.startsWith("LUT_3D_SIZE")) {
      size = parseInt(line.split(/\s+/)[1], 10);
      continue;
    }
    if (line.startsWith("DOMAIN_")) continue;
    const parts = line.split(/\s+/).map(Number);
    if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
      values.push(...parts);
    }
  }
  if (!size) throw new Error("LUT inválida: falta LUT_3D_SIZE");
  const expected = size * size * size * 3;
  if (values.length !== expected) {
    throw new Error(`LUT corrupta: ${values.length / 3} muestras, esperaba ${size ** 3}`);
  }
  return { size, title, data: new Float32Array(values) };
}

export async function fetchCubeLut(url: string): Promise<CubeLut> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`No se pudo cargar LUT: ${url}`);
  const text = await r.text();
  return parseCubeLut(text);
}

/**
 * Sube la LUT como textura 3D RGBA8.
 * El intérprete asume valores [0..1] (clampea), suficiente para SDR.
 */
export function uploadLut3D(
  gl: WebGL2RenderingContext, lut: CubeLut,
): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("createTexture devolvió null");

  const n = lut.size;
  const u8 = new Uint8Array(n * n * n * 4);
  for (let i = 0; i < n * n * n; i++) {
    u8[i * 4]     = Math.round(Math.max(0, Math.min(1, lut.data[i * 3]))     * 255);
    u8[i * 4 + 1] = Math.round(Math.max(0, Math.min(1, lut.data[i * 3 + 1])) * 255);
    u8[i * 4 + 2] = Math.round(Math.max(0, Math.min(1, lut.data[i * 3 + 2])) * 255);
    u8[i * 4 + 3] = 255;
  }

  gl.bindTexture(gl.TEXTURE_3D, tex);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
  gl.texImage3D(
    gl.TEXTURE_3D, 0, gl.RGBA8,
    n, n, n, 0,
    gl.RGBA, gl.UNSIGNED_BYTE, u8,
  );
  return tex;
}
